import { Button, Empty, Form, Input, Spin } from "antd";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { FiMail, FiPhone, FiSearch } from "react-icons/fi";
import { HiOutlineChevronRight } from "react-icons/hi";
import { CheckCircleFilled, SafetyOutlined, UserAddOutlined } from "@ant-design/icons";
import { PROVIDER_LABELS, getInsuranceCustomerUserId } from "../../../lib/insuranceSaleApi";
import cn from "../../../../../utilities/tailwind-merge";

const AVATAR_COLORS = [
    { bg: "#dbeafe", text: "#1d4ed8" },
    { bg: "#dcfce7", text: "#15803d" },
    { bg: "#f3e8ff", text: "#7e22ce" },
    { bg: "#fef9c3", text: "#a16207" },
    { bg: "#ffe4e6", text: "#be123c" },
    { bg: "#e0e7ff", text: "#4338ca" },
];

const getInitials = (user) => {
    const first = user?.firstName?.[0] ?? "";
    const last = user?.lastName?.[0] ?? "";
    return `${first}${last}`.toUpperCase() || "?";
};

const getFullName = (user) => `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim() || "—";

/** One tap per provider instead of a dropdown: there are only ever one or two. */
const ProviderPicker = ({ value, onChange, options }) => (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-3" role="radiogroup">
        {options.map((option) => {
            const isSelected = option === value;
            return (
                <button
                    key={option}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    onClick={() => onChange?.(option)}
                    className={cn(
                        "flex min-h-[56px] items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors",
                        isSelected
                            ? "border-[#5D5DF6] bg-[#F5F5FF] ring-1 ring-[#5D5DF6]"
                            : "border-gray-200 bg-white hover:border-[#5D5DF6]/50",
                    )}>
                    <span
                        className={cn(
                            "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-base",
                            isSelected ? "bg-[#5D5DF6] text-white" : "bg-gray-100 text-gray-500",
                        )}>
                        <SafetyOutlined />
                    </span>
                    <span className="min-w-0 flex-1 text-base font-semibold text-gray-900">
                        {PROVIDER_LABELS[option] ?? option}
                    </span>
                    {isSelected && <CheckCircleFilled className="text-lg text-[#5D5DF6]" />}
                </button>
            );
        })}
    </div>
);

ProviderPicker.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func,
    options: PropTypes.arrayOf(PropTypes.string).isRequired,
};

ProviderPicker.defaultProps = {
    value: undefined,
    onChange: undefined,
};

const CustomerSearchList = ({
    availableProviders,
    users,
    loading,
    searchQuery,
    onSearchChange,
    selectedUserId,
    onSelect,
    onAddCustomer,
}) => {
    const { t } = useTranslation("sellInsurance");

    return (
        <div className="space-y-5">
            <Form.Item
                label={<span className="text-base font-semibold text-gray-900">{t("wizard.provider")}</span>}
                name="provider"
                className="mb-0"
                rules={[{ required: true, message: t("wizard.fieldRequired") }]}>
                <ProviderPicker options={availableProviders} />
            </Form.Item>

            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                <div className="border-b border-gray-100 px-3 py-3 sm:px-4">
                    <p className="mb-2 text-base font-semibold text-gray-900">{t("wizard.searchExistingCustomer")}</p>
                    <div className="flex flex-col gap-2 sm:flex-row">
                        <Input
                            size="large"
                            prefix={<FiSearch className="text-lg text-gray-400" />}
                            placeholder={t("wizard.customerPlaceholder")}
                            value={searchQuery}
                            onChange={(event) => onSearchChange(event.target.value)}
                            allowClear
                            className="w-full !rounded-xl"
                        />

                        <Button
                            size="large"
                            type="primary"
                            icon={<UserAddOutlined />}
                            onClick={onAddCustomer}
                            className="!h-10 w-full shrink-0 !rounded-xl sm:w-auto">
                            {t("wizard.addCustomer")}
                        </Button>
                    </div>
                </div>

                <div className="min-w-0">
                    <div className="border-b border-gray-100 bg-gray-50/60 px-4 py-2">
                        <p className="mb-0 text-sm font-medium text-gray-500">
                            {searchQuery.trim()
                                ? t("wizard.showingResults", { count: users.length, query: searchQuery.trim() })
                                : t("wizard.showingResultsAll", { count: users.length })}
                        </p>
                    </div>

                    <div className="max-h-[min(50dvh,420px)] overflow-y-auto p-2 sm:max-h-[420px]">
                        {loading ? (
                            <div className="flex justify-center py-8">
                                <Spin size="large" />
                            </div>
                        ) : users.length === 0 ? (
                            <Empty description={t("wizard.noCustomersFound")} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        ) : (
                            <div className="space-y-1">
                                {users.map((user, index) => {
                                    const userId = getInsuranceCustomerUserId(user);
                                    const isSelected = userId === selectedUserId;
                                    const avatarColor = AVATAR_COLORS[index % AVATAR_COLORS.length];

                                    return (
                                        <button
                                            key={userId}
                                            type="button"
                                            onClick={() => onSelect(userId)}
                                            aria-pressed={isSelected}
                                            className={cn(
                                                "flex min-h-[64px] w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors",
                                                isSelected
                                                    ? "border-[#5D5DF6] bg-[#F5F5FF] ring-1 ring-[#5D5DF6]"
                                                    : "border-transparent bg-white hover:border-gray-200 hover:bg-gray-50",
                                            )}>
                                            <div
                                                className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-sm font-semibold"
                                                style={{
                                                    backgroundColor: avatarColor.bg,
                                                    color: avatarColor.text,
                                                }}>
                                                {getInitials(user)}
                                            </div>

                                            <div className="min-w-0 flex-1">
                                                <p className="m-0 truncate text-base font-semibold text-gray-900">
                                                    {getFullName(user)}
                                                </p>
                                                <div className="mt-0.5 flex flex-col gap-0.5 text-sm text-gray-500 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-3 sm:gap-y-0.5">
                                                    {user.mobileNumber ? (
                                                        <span className="inline-flex min-w-0 items-center gap-1">
                                                            <FiPhone className="shrink-0" />
                                                            <span className="truncate">{user.mobileNumber}</span>
                                                        </span>
                                                    ) : null}
                                                    {user.email ? (
                                                        <span className="inline-flex min-w-0 items-center gap-1">
                                                            <FiMail className="shrink-0" />
                                                            <span className="truncate">{user.email}</span>
                                                        </span>
                                                    ) : null}
                                                </div>
                                            </div>

                                            {isSelected ? (
                                                <CheckCircleFilled className="flex-shrink-0 text-xl text-[#5D5DF6]" />
                                            ) : (
                                                <HiOutlineChevronRight className="flex-shrink-0 text-lg text-gray-300" />
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

CustomerSearchList.propTypes = {
    availableProviders: PropTypes.arrayOf(PropTypes.string).isRequired,
    users: PropTypes.arrayOf(PropTypes.object).isRequired,
    loading: PropTypes.bool,
    searchQuery: PropTypes.string.isRequired,
    onSearchChange: PropTypes.func.isRequired,
    selectedUserId: PropTypes.string,
    onSelect: PropTypes.func.isRequired,
    onAddCustomer: PropTypes.func.isRequired,
};

CustomerSearchList.defaultProps = {
    loading: false,
    selectedUserId: undefined,
};

export default CustomerSearchList;
